/**
 * Slots, numbering and letter normalisation of a crossword grid. Pure functions, a port of
 * `backend/apps/puzzletypes/crossword/grid.py`; the shared fixtures carry the expected slots
 * so both stay in step (ADR 0032).
 */
import { BLOCK, EMPTY, type Cell, type Direction, type Slot } from "./types";

export function size(grid: string[]): { rows: number; cols: number } {
  return { rows: grid.length, cols: grid[0]?.length ?? 0 };
}

export function isRectangular(grid: string[]): boolean {
  return grid.length > 0 && grid.every((row) => row.length === grid[0].length);
}

export function isLetterCell(grid: string[], r: number, c: number): boolean {
  const row = grid[r];
  return row !== undefined && c >= 0 && c < row.length && row[c] !== BLOCK;
}

/** Every slot with its number: the across slots in number order, then the down ones. */
export function slotsOf(grid: string[]): Slot[] {
  const { rows, cols } = size(grid);
  const across: Slot[] = [];
  const down: Slot[] = [];
  let number = 0;
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      if (!isLetterCell(grid, r, c)) continue;
      const startsAcross = !isLetterCell(grid, r, c - 1) && isLetterCell(grid, r, c + 1);
      const startsDown = !isLetterCell(grid, r - 1, c) && isLetterCell(grid, r + 1, c);
      if (!startsAcross && !startsDown) continue;
      number += 1;
      if (startsAcross) {
        let length = 0;
        while (isLetterCell(grid, r, c + length)) length += 1;
        across.push({ direction: "across", start: [r, c], length, number });
      }
      if (startsDown) {
        let length = 0;
        while (isLetterCell(grid, r + length, c)) length += 1;
        down.push({ direction: "down", start: [r, c], length, number });
      }
    }
  }
  return [...across, ...down];
}

export function cellsOf(slot: Slot): Cell[] {
  const [r, c] = slot.start;
  return Array.from({ length: slot.length }, (_, i) =>
    slot.direction === "across" ? ([r, c + i] as Cell) : ([r + i, c] as Cell),
  );
}

/** How a clue names its slot: the direction and the start cell, never the number. */
export function slotKey(direction: Direction, start: Cell): string {
  return `${direction}:${start[0]},${start[1]}`;
}

/** The slot of that direction that contains the cell, if any. */
export function slotAt(slots: Slot[], cell: Cell, direction: Direction): Slot | null {
  for (const slot of slots) {
    if (slot.direction !== direction) continue;
    const [r, c] = slot.start;
    if (direction === "across" && cell[0] === r && cell[1] >= c && cell[1] < c + slot.length)
      return slot;
    if (direction === "down" && cell[1] === c && cell[0] >= r && cell[0] < r + slot.length)
      return slot;
  }
  return null;
}

/** Letter cells that belong to no slot: nothing could ever clue them. */
export function unslottedCells(grid: string[], slots: Slot[]): Cell[] {
  const covered = new Set(slots.flatMap((slot) => cellsOf(slot).map((c) => `${c[0]},${c[1]}`)));
  const { rows, cols } = size(grid);
  const found: Cell[] = [];
  for (let r = 0; r < rows; r += 1)
    for (let c = 0; c < cols; c += 1)
      if (isLetterCell(grid, r, c) && !covered.has(`${r},${c}`)) found.push([r, c]);
  return found;
}

/** `A`…`Z`, then `AA`…: the column names of the French coordinates layout. */
export function columnLabel(index: number): string {
  let label = "";
  let n = index + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    label = String.fromCharCode(65 + rem) + label;
    n = Math.floor((n - 1) / 26);
  }
  return label;
}

/**
 * One typed character as the plain uppercase letter it stands for, or `""` when it is not a
 * single A–Z letter once its diacritics are removed (a digit, `ß`, `Œ`).
 */
export function normalizeLetter(char: string): string {
  const base = char.normalize("NFD").replace(/\p{M}/gu, "").normalize("NFC").toUpperCase();
  return /^[A-Z]$/.test(base) ? base : "";
}

/** Uppercase, diacritics removed; blocks and empty cells pass through untouched. */
export function normalizeRow(row: string): string {
  return Array.from(row, (char) => {
    if (char === BLOCK || char === EMPTY) return char;
    return char.normalize("NFD").replace(/\p{M}/gu, "").normalize("NFC").toUpperCase();
  }).join("");
}

export function blankRows(grid: string[]): string[] {
  return grid.map((row) => Array.from(row, (cell) => (cell === BLOCK ? BLOCK : EMPTY)).join(""));
}

/**
 * Any row set, however wrong, as letters in the exact shape of the grid: blocks where the grid
 * has them, one plain letter or `.` everywhere else, cropped or padded to the size.
 */
export function conformRows(source: readonly unknown[] | undefined, grid: string[]): string[] {
  return grid.map((mask, r) => {
    const raw = source?.[r];
    const row = typeof raw === "string" ? raw : "";
    return Array.from(mask, (cell, c) => {
      if (cell === BLOCK) return BLOCK;
      const char = row[c];
      if (char === undefined || char === BLOCK || char === EMPTY) return EMPTY;
      return normalizeLetter(char) || EMPTY;
    }).join("");
  });
}

export function isComplete(rows: string[]): boolean {
  return rows.every((row) => !row.includes(EMPTY));
}

export function emptyCellCount(rows: string[]): number {
  return rows.reduce((acc, row) => acc + row.split(EMPTY).length - 1, 0);
}

/** Row `r` (1-based) or column letter, for the coordinates layout. */
export function coordinateLabel(direction: Direction, start: Cell): string {
  return direction === "across" ? String(start[0] + 1) : columnLabel(start[1]);
}
